import React from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

import { IStudent } from "../../../state";
import { ReactComponent as ArrowDownIcon } from "../../../assets/svg/arrow_down.svg";
import { ReactComponent as ArrowRight3Icon } from "../../../assets/svg/arrow_right_3.svg";
import { ReactComponent as StudentProfileIcon } from "../../../assets/svg/student_profile.svg";
import { ReactComponent as ClassDataIcon } from "../../../assets/svg/class-data.svg";
import { ReactComponent as LessonCompleteIcon } from "../../../assets/svg/lesson_complete.svg";
import { ReactComponent as PinIcon } from "../../../assets/svg/pin.svg";
import Sidebar from "../../../organisms/teacher/Sidebar";
import { generateFullName, getPasswordDisplay } from "../../../libs/Util";
import useOnClickOutside from "../../../hooks/useOnClickOutside";

import styles from "./StudentList.module.scss";

export type Props = {
  surveyEnabled: boolean;
  listStudents: IStudent[];
  nameClass: string;
  isActiveDownloadMenu?: boolean;
  goBack: () => void;
  goToDownloadPDF: () => void;
  goToDownloadCSV: () => void;
  goToDownloadLeafPDF: () => void;
  goToStudentResponse: (student: IStudent) => void;
};

type SortOrder = "kana" | "student_no";

const BackDrop: React.FC<{ onClick: () => void }> = ({ onClick }) =>
  createPortal(
    <div className={styles.backdrop} onClick={onClick} />,
    document.body,
  );

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: "kana", label: "50音順" },
  { value: "student_no", label: "学籍番号順" },
];

const StudentList: React.FC<Props> = ({
  listStudents,
  nameClass,
  isActiveDownloadMenu,
  goBack,
  goToDownloadPDF,
  goToDownloadCSV,
  goToDownloadLeafPDF,
  goToStudentResponse,
}) => {
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("kana");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useOnClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  const sortedStudents = React.useMemo(() => {
    const collator = new Intl.Collator(["ja", "en"], { numeric: true });
    return [...listStudents].sort((a, b) => {
      if (sortOrder === "student_no") {
        if (!a.student_no_in_school) return 1;
        if (!b.student_no_in_school) return -1;
        return collator.compare(a.student_no_in_school, b.student_no_in_school);
      }
      const aKana = `${a.last_name_kana}${a.first_name_kana}`;
      const bKana = `${b.last_name_kana}${b.first_name_kana}`;
      return aKana.localeCompare(bKana, "ja");
    });
  }, [listStudents, sortOrder]);

  const currentLabel =
    SORT_OPTIONS.find(o => o.value === sortOrder)?.label ?? "";

  return (
    <div className={styles.pageLayout}>
      <Sidebar
        classList={nameClass ? [nameClass] : []}
        currentMaterials={[]}
        viewingMaterialId={null}
        activeModalId={null}
        activeTab="core"
        targetClassIds={[]}
        showTabs={false}
        hasMiddle={false}
        hasHigh={false}
        hasCore={false}
        hasBase={false}
        onBackToClass={goBack}
        onTabClick={() => undefined}
        onItemClick={() => undefined}
      />

      <div className={styles.main}>
        {isActiveDownloadMenu && (
          <section className={styles.downloadSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIconWrapper}>
                <ClassDataIcon className={styles.sectionIcon} />
              </div>
              <span>ダウンロードしたいデータを選んでください</span>
            </div>
            <div className={styles.downloadCards}>
              <div
                className={styles.downloadCard}
                onClick={goToDownloadLeafPDF}
              >
                <div className={styles.downloadCardInner}>
                  <div className={styles.downloadCardBody}>
                    <p className={styles.downloadCardTitle}>実践ワークシート</p>
                    <div className={styles.downloadCardDescRow}>
                      <PinIcon className={styles.pinIcon} />
                      <p className={styles.downloadCardDesc}>
                        実践ワークシートに入力した内容を
                        <br />
                        1枚にまとめたPDFデータ
                      </p>
                    </div>
                  </div>
                  <ArrowRight3Icon className={styles.downloadArrow} />
                </div>
              </div>
              <div className={styles.downloadCard} onClick={goToDownloadPDF}>
                <div className={styles.downloadCardInner}>
                  <div className={styles.downloadCardBody}>
                    <p className={styles.downloadCardTitle}>振り返りシート</p>
                    <div className={styles.downloadCardDescRow}>
                      <PinIcon className={styles.pinIcon} />
                      <p className={styles.downloadCardDesc}>
                        vol.ごとの生徒の学びと変化を
                        <br />
                        1枚にまとめたPDFデータ
                      </p>
                    </div>
                  </div>
                  <ArrowRight3Icon className={styles.downloadArrow} />
                </div>
              </div>
              <div className={styles.downloadCard} onClick={goToDownloadCSV}>
                <div className={styles.downloadCardInner}>
                  <div className={styles.downloadCardBody}>
                    <p className={styles.downloadCardTitle}>回答のCSVデータ</p>
                    <div className={styles.downloadCardDescRow}>
                      <PinIcon className={styles.pinIcon} />
                      <p className={styles.downloadCardDesc}>
                        生徒データを細かく分析したい
                        <br />
                        先生向けのCSVデータ
                      </p>
                    </div>
                  </div>
                  <ArrowRight3Icon className={styles.downloadArrow} />
                </div>
              </div>
            </div>
          </section>
        )}

        <section className={styles.studentSectionWrapper}>
          <div className={styles.studentSectionHeader}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIconWrapper}>
                <ClassDataIcon className={styles.sectionIcon} />
              </div>
              <span>回答を確認したい生徒を選んでください</span>
            </div>
            <div
              className={clsx(
                styles.sortDropdown,
                isDropdownOpen && styles.open,
              )}
              ref={dropdownRef}
            >
              <button
                className={styles.sortTrigger}
                onClick={() => setIsDropdownOpen(prev => !prev)}
              >
                <span className={styles.sortTriggerText}>{currentLabel}</span>
                <div className={styles.sortArrowWrapper}>
                  <ArrowDownIcon className={styles.sortArrowIcon} />
                </div>
              </button>
              {isDropdownOpen && (
                <div className={styles.sortMenu}>
                  <div className={styles.sortMenuInner}>
                    {SORT_OPTIONS.map((option, idx) => (
                      <React.Fragment key={option.value}>
                        {idx > 0 && <div className={styles.sortMenuDivider} />}
                        <div
                          className={styles.sortMenuItem}
                          onClick={() => {
                            setSortOrder(option.value);
                            setIsDropdownOpen(false);
                          }}
                        >
                          <span
                            className={clsx(
                              styles.sortMenuItemText,
                              sortOrder === option.value &&
                                styles.sortMenuItemTextActive,
                            )}
                          >
                            {option.label}
                          </span>
                          {sortOrder === option.value && (
                            <LessonCompleteIcon
                              className={styles.sortCheckIcon}
                            />
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={styles.studentSection}>
            <div className={styles.studentList}>
              {sortedStudents.map(student => (
                <div
                  key={student.id}
                  className={styles.studentCard}
                  onClick={() => goToStudentResponse(student)}
                >
                  <div className={styles.studentAvatar}>
                    {student.photo?.url ? (
                      <img src={student.photo.url} alt="" />
                    ) : (
                      <StudentProfileIcon />
                    )}
                  </div>
                  <div className={styles.studentInfo}>
                    <div className={styles.studentNameRow}>
                      <span className={styles.studentName}>
                        {generateFullName(
                          [student.last_name, student.first_name],
                          "",
                        )}
                      </span>
                      <span className={styles.studentKana}>
                        {student.last_name_kana}
                        {student.first_name_kana}
                      </span>
                    </div>
                    <div className={styles.studentMeta}>
                      <span className={styles.studentNo}>
                        学籍番号：{student.student_no_in_school}
                      </span>
                      <span>ログインID：{student.identity}</span>
                      <span>
                        {getPasswordDisplay(student.temporary_password)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      {isDropdownOpen && <BackDrop onClick={() => setIsDropdownOpen(false)} />}
    </div>
  );
};

export default StudentList;
